import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTenant } from "@/lib/tenant-context";
import { platformService } from "@/services";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import {
  FALLBACK_PRIVACY_TEMPLATE,
  FALLBACK_TERMS_TEMPLATE,
  renderPolicyTemplate,
  buildPolicyFields,
} from "@/lib/policy-templates";

interface PolicyPageProps {
  type: "privacy" | "terms";
}

const PolicyPage = ({ type }: PolicyPageProps) => {
  const { tenant_slug } = useParams<{ tenant_slug: string }>();
  const navigate = useNavigate();
  const { tenant, loading } = useTenant();
  const [templateHtml, setTemplateHtml] = useState<string | null>(null);
  const [templateLoading, setTemplateLoading] = useState(true);

  const templateKey = type === "privacy" ? "privacy_policy" : "terms_conditions";
  const fallback = type === "privacy" ? FALLBACK_PRIVACY_TEMPLATE : FALLBACK_TERMS_TEMPLATE;

  useEffect(() => {
    const fetchTemplate = async () => {
      setTemplateLoading(true);
      const { data, error } = await platformService.getPolicyTemplateByKey(templateKey);

      if (error || !data) {
        setTemplateHtml(fallback);
      } else {
        setTemplateHtml(data.template_html);
      }
      setTemplateLoading(false);
    };
    fetchTemplate();
  }, [templateKey]);

  if (loading || templateLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const fields = tenant
    ? buildPolicyFields({
        name: tenant.brandName,
        notificationEmail: tenant.notificationEmail,
        freeAttemptsPerUser: tenant.freeAttemptsPerUser,
        photoRetentionEnabled: (tenant as any).photoRetentionEnabled,
        photoRetentionDays: (tenant as any).photoRetentionDays,
      })
    : {};

  const content = renderPolicyTemplate(templateHtml || fallback, fields);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/t/${tenant_slug}`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="font-display font-semibold text-sm">
          {type === "privacy" ? "Privacy Policy" : "Terms & Conditions"}
        </span>
      </div>

      <div className="flex-1 px-4 py-8">
        <article
          className="prose prose-sm max-w-2xl mx-auto
            prose-headings:font-display prose-headings:text-foreground
            prose-p:text-muted-foreground prose-li:text-muted-foreground
            prose-strong:text-foreground prose-a:text-primary"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, { USE_PROFILES: { html: true } }) }}
        />
      </div>

      <footer className="border-t py-4 text-center text-xs text-muted-foreground">
        {tenant?.brandName}
      </footer>
    </div>
  );
};

export default PolicyPage;
